import { getApiUserHandlers } from 'entities-next/user'
import { flow, isEmpty } from 'lodash'
import { emptyStringToNull, normalizeDataForApi } from 'utils-next/api'
import { FormValues, parseNilToEmptyString, pickOrCreate } from 'utils-next/forms'
import * as Yup from 'yup'

import { ApiCreateScoutingCampaignData, ScoutingCampaign } from '../../../../../../../../entities-next/scoutingCampaign'

const keysFromScoutingCampaign = [
  'id',
  'name',
  'description',
  'status',
  'editable',
  'positiveKeywords',
  'negativeKeywords',
  'corporateId',
  'type',
  'assignedOpsLeadId',
  'internalPositivePrimaryKeywordsSets',
  'internalPositiveSecondaryKeywords',
  'internalNegativePrimaryKeywords',
  'internalNegativeSecondaryKeywords',
  'internalCategories',
  'internalNegativeCategories',
  'internalLocations',
  'internalNegativeLocations',
  'internalStages',
  'internalMinimumYear',
  'internalMaximumYear',
  'internalIncludeCompaniesWithoutMaturityInformation',
  'dailyCompanyLimit',
  'hoursBetweenBatchesOfPairings',
  'sampleCompanies',
  'sampleCompaniesText',
  'radarScouting',
  'organizationHeadquarters',
  'negativeOrganizationHeadquarters',
  'organizationFoundedAt',
  'organizationStages',
  'relaxedSweetSpotDefinition',
  'includeCompaniesWithoutMaturityInformation',
  'packageCountable',
  'isDeleted',
]

export type ScoutingCampaignFormData = FormValues<
  Pick<
    ScoutingCampaign,
    | 'id'
    | 'name'
    | 'description'
    | 'status'
    | 'editable'
    | 'positiveKeywords'
    | 'negativeKeywords'
    | 'corporateId'
    | 'type'
    | 'assignedOpsLeadId'
    | 'internalPositivePrimaryKeywordsSets'
    | 'internalPositiveSecondaryKeywords'
    | 'internalNegativePrimaryKeywords'
    | 'internalNegativeSecondaryKeywords'
    | 'internalCategories'
    | 'internalNegativeCategories'
    | 'internalLocations'
    | 'internalNegativeLocations'
    | 'internalStages'
    | 'internalMinimumYear'
    | 'internalMaximumYear'
    | 'internalIncludeCompaniesWithoutMaturityInformation'
    | 'dailyCompanyLimit'
    | 'hoursBetweenBatchesOfPairings'
    | 'sampleCompanies'
    | 'sampleCompaniesText'
    | 'radarScouting'
    | 'organizationHeadquarters'
    | 'negativeOrganizationHeadquarters'
    | 'organizationFoundedAt'
    | 'organizationStages'
    | 'relaxedSweetSpotDefinition'
    | 'includeCompaniesWithoutMaturityInformation'
    | 'packageCountable'
    | 'isDeleted'
  >
> & {
  userId?: ApiCreateScoutingCampaignData['userId']
}

export const scoutingCampaignDataFormValidationSchema = Yup.object().shape({
  name: Yup.string().max(70).required(),
  description: Yup.string().max(10000).required(),
  status: Yup.string(),
  positiveKeywords: Yup.string().matches(/(.+?)(?:,|$)/g, 'Values must be comma separated'),
  negativeKeywords: Yup.string().matches(/(.+?)(?:,|$)/g, 'Values must be comma separated'),
  assignedOpsLead: Yup.number().nullable(),
  internalPositivePrimaryKeywordsSets: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalPositiveSecondaryKeywords: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalNegativePrimaryKeywords: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalNegativeSecondaryKeywords: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalCategories: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalNegativeCategories: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalLocations: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  internalStages: Yup.string()
    .matches(/(.+?)(?:,|$)/g, 'Values must be comma separated')
    .nullable(),
  minimumYear: Yup.number().nullable(),
  maximumYear: Yup.number().nullable(),
})

export function parseScoutingCampaignToScoutingCampaignFormData({
  ...scoutingCampaignData
}: ScoutingCampaign): ScoutingCampaignFormData {
  const formData = flow(pickOrCreate(keysFromScoutingCampaign), parseNilToEmptyString)(scoutingCampaignData)
  return {
    ...formData,
    internalLocations: formData.internalLocations
      ? formData.internalLocations
          .toString()
          .split(',')
          .map((w) => w.trim())
          .join(', ')
      : '',
    internalNegativeLocations: formData.internalNegativeLocations
      ? formData.internalNegativeLocations
          .toString()
          .split(',')
          .map((w) => w.trim())
          .join(', ')
      : '',
    internalStages: formData.internalStages
      ? formData.internalStages
          .toString()
          .split(',')
          .map((w) => w.trim())
          .join(', ')
      : '',
  }
}

export async function parseScoutingCampaignFormDataToScoutingCampaign(
  data: ScoutingCampaignFormData
): Promise<ApiCreateScoutingCampaignData> {
  // TODO: important! for some reason we are receiving assignedOpsLeadId as an object (the assigned user), is necessary to check this
  const assignedOpsLead = data.assignedOpsLeadId as any
  const assignedOpsLeadId =
    !isEmpty(assignedOpsLead) && isNaN(assignedOpsLead) ? assignedOpsLead.id : data.assignedOpsLeadId

  const { apiGetUser } = getApiUserHandlers()
  const userId = data.userId ? Number(data.userId) : undefined
  let user

  if (userId) {
    user = await apiGetUser(Number(data.userId))
  }

  return {
    ...normalizeDataForApi(data),
    dailyCompanyLimit: Number(data.dailyCompanyLimit),
    hoursBetweenBatchesOfPairings: Number(data.hoursBetweenBatchesOfPairings),
    assignedOpsLeadId: emptyStringToNull(assignedOpsLeadId),
    positiveKeywords: data.positiveKeywords ?? '',
    negativeKeywords: data.negativeKeywords ?? '',
    internalPositivePrimaryKeywordsSets: data.internalPositivePrimaryKeywordsSets ?? '',
    internalPositiveSecondaryKeywords: data.internalPositiveSecondaryKeywords ?? '',
    internalNegativePrimaryKeywords: data.internalNegativePrimaryKeywords ?? '',
    internalNegativeSecondaryKeywords: data.internalNegativeSecondaryKeywords ?? '',
    internalLocations: !isEmpty(data.internalLocations) ? data.internalLocations : [],
    internalNegativeLocations: !isEmpty(data.internalNegativeLocations) ? data.internalNegativeLocations : [],
    internalMaximumYear: emptyStringToNull(data.internalMaximumYear),
    internalMinimumYear: emptyStringToNull(data.internalMinimumYear),
    internalStages: !isEmpty(data.internalStages) ? data.internalStages : [],
    userId: userId ? userId : undefined,
    corporateId: user ? user.corporateId : data.corporateId,
  }
}
